import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    const [cartId, setCartId] = useState(null);
    const isMountedRef = useRef(true);
    
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

  useEffect(() => {
      if (currentUser) {
            setCartId(currentUser.uid);
        } else {
            let guestCartId = localStorage.getItem('guestCartId');
            if (!guestCartId) {
                guestCartId = uuidv4();
                localStorage.setItem('guestCartId', guestCartId);
            }
            setCartId(guestCartId);
        }
    }, [currentUser]);

    useEffect(() => {
        if (!cartId) return;

        const loadCart = async () => {
            setLoading(true);
            const cartDocRef = doc(db, 'carts', cartId);
            const docSnap = await getDoc(cartDocRef);
            if (docSnap.exists()) {
                setCartItems(docSnap.data().items || []);
      } else {
        setCartItems([]);
      }
      setLoading(false);
    };

    loadCart();
    }, [cartId]);

    useEffect(() => {
        if (!cartId || loading) return;
        
        const saveCart = async () => {
            if (!isMountedRef.current) return;
            
            try {
                const cartDocRef = doc(db, 'carts', cartId);
                await setDoc(cartDocRef, { items: cartItems }, { merge: true });
                console.log('[CartContext] cartItems updated:', cartItems);
            } catch (error) {
                console.error('Error saving cart:', error);
            }
        };
        
        const timeoutId = setTimeout(saveCart, 500); // Debounce saves
        
        return () => {
            clearTimeout(timeoutId);
        };
    }, [cartItems, cartId, loading]);

    const addToCart = (product) => {
        console.log('[CartContext] addToCart called with:', product);
        setCartItems(prevItems => {
            const existingItem = prevItems.find(item => item.id === product.id && item.selectedWeight === product.selectedWeight && item.selectedBeanType === product.selectedBeanType);
      if (existingItem) {
                return prevItems.map(item =>
                    item.id === product.id && item.selectedWeight === product.selectedWeight && item.selectedBeanType === product.selectedBeanType
                        ? { ...item, quantity: item.quantity + product.quantity }
            : item
        );
            }
            return [...prevItems, product];
        });
    };

    const removeFromCart = (productId, weight, type) => {
        setCartItems(prevItems => prevItems.filter(item =>
            !(item.id === productId && item.selectedWeight === weight && item.selectedBeanType === type)
        ));
    };

    const updateQuantity = (productId, weight, type, quantity) => {
        if (quantity <= 0) {
            removeFromCart(productId, weight, type);
            return;
        }
        setCartItems(prevItems => prevItems.map(item =>
            (item.id === productId && item.selectedWeight === weight && item.selectedBeanType === type)
          ? { ...item, quantity }
          : item
        ));
  };

    const clearCart = () => {
    setCartItems([]);
    };

    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
        totalItems,
        totalPrice,
        loading,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};